'use client';

import type { ProductCard } from '@repo/shopify';
import { useEffect, useMemo, useState } from 'react';
import { fetchProductBlockProducts, type ProductBlockRequest } from './product-block-products';
import { ProductCarouselClient } from './product-carousel-client';

interface ProductCarouselBlockClientProps {
	request: ProductBlockRequest;
	initialProducts?: ProductCard[];
	heading?: string;
	sectionId?: string;
	subheading?: string;
	showQuickAdd?: boolean;
	ctaButton?: { label?: string; url?: string };
	sectionSpacing?: string;
	priorityImageCount?: number;
	moneyLocale?: string;
	fetchOnMount?: boolean;
}

function ProductCarouselSkeleton() {
	return (
		<div className="relative overflow-hidden">
			<div className="-ml-3 flex md:-ml-4">
				{Array.from({ length: 4 }, (_, index) => `carousel-skeleton-${index}`).map((key) => (
					<div
						key={key}
						className="min-w-0 shrink-0 basis-[70%] pl-3 sm:basis-[45%] md:basis-1/3 md:pl-4 lg:basis-1/4"
					>
						<div className="relative aspect-[3/4] overflow-hidden rounded bg-neutral-100">
							<div className="absolute inset-0 animate-pulse bg-neutral-200" />
						</div>
						<div className="mt-3 min-h-[4.25rem]">
							<div className="h-4 w-10 animate-pulse rounded bg-neutral-200" />
							<div className="mt-2 h-4 w-3/4 animate-pulse rounded bg-neutral-200" />
							<div className="mt-2 h-4 w-16 animate-pulse rounded bg-neutral-200" />
						</div>
					</div>
				))}
			</div>
		</div>
	);
}

export function ProductCarouselBlockClient({
	request,
	initialProducts,
	heading,
	sectionId,
	subheading,
	showQuickAdd,
	ctaButton,
	sectionSpacing,
	priorityImageCount,
	moneyLocale,
	// Server-rendered `initialProducts` are authoritative; only live preview refetches.
	fetchOnMount = false,
}: ProductCarouselBlockClientProps) {
	const initialProductState =
		initialProducts && initialProducts.length > 0 ? initialProducts : null;
	const [products, setProducts] = useState<ProductCard[] | null>(initialProductState);
	const requestKey = useMemo(() => JSON.stringify(request), [request]);

	useEffect(() => {
		if (!fetchOnMount) {
			setProducts(initialProductState);
			return;
		}

		const controller = new AbortController();
		const nextRequest = JSON.parse(requestKey) as ProductBlockRequest;

		setProducts(initialProductState);
		void fetchProductBlockProducts(nextRequest, controller.signal)
			.then((nextProducts) => {
				if (!controller.signal.aborted) {
					if (nextProducts.length === 0 && initialProducts && initialProducts.length > 0) {
						return;
					}

					setProducts(nextProducts);
				}
			})
			.catch(() => {
				if (!controller.signal.aborted) {
					if (initialProducts && initialProducts.length > 0) {
						return;
					}

					setProducts([]);
				}
			});

		return () => controller.abort();
	}, [fetchOnMount, initialProductState, initialProducts, requestKey]);

	return (
		<ProductCarouselClient
			products={products ?? []}
			heading={heading}
			sectionId={sectionId}
			subheading={subheading}
			showQuickAdd={showQuickAdd}
			ctaButton={ctaButton}
			sectionSpacing={sectionSpacing}
			priorityImageCount={priorityImageCount}
			moneyLocale={moneyLocale}
			// Only live preview refetches; on the published path an empty server
			// result must not prerender a skeleton that nothing will ever replace.
			isLoading={products === null && fetchOnMount}
			loadingFallback={<ProductCarouselSkeleton />}
		/>
	);
}
